import { CommonService } from 'dashboard-services/services';

const unwrapQuotedValue = value => {
  const trimmed = String(value || "").trim()

  if(trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/""/g, '"')
  }

  return trimmed.replace(/""/g, '"')
}

const getTopLevelAmpersandIndex = value => {
  let inQuotes = false
  let parenthesesDepth = 0

  for(let index = 0; index < value.length; index++) {
    const char = value[index]
    const nextChar = value[index + 1]

    if(char === '"' && inQuotes && nextChar === '"') {
      index++
    } else if(char === '"') {
      inQuotes = !inQuotes
    } else if(!inQuotes && char === '(') {
      parenthesesDepth++
    } else if(!inQuotes && char === ')' && parenthesesDepth > 0) {
      parenthesesDepth--
    } else if(!inQuotes && parenthesesDepth === 0 && char === '&') {
      return index
    }
  }

  return -1
}

export const splitFormulaArguments = argsString => {
  const params = []
  let current = ""
  let inQuotes = false
  let parenthesesDepth = 0

  const pushCurrent = () => {
    params.push(current.trim())
    current = ""
  }

  for(let index = 0; index < argsString.length; index++) {
    const char = argsString[index]
    const nextChar = argsString[index + 1]
    const isEscapedQuote = char === '"' && inQuotes && nextChar === '"'

    if(isEscapedQuote) {
      current += '""'
      index++
    } else if(char === '"') {
      inQuotes = !inQuotes
      current += char
    } else if(!inQuotes && char === '(') {
      parenthesesDepth++
      current += char
    } else if(!inQuotes && char === ')' && parenthesesDepth > 0) {
      parenthesesDepth--
      current += char
    } else if(char === "," && !inQuotes && parenthesesDepth === 0) {
      pushCurrent()
    } else {
      current += char
    }
  }

  if(current.length > 0 || argsString.trim() === "") {
    pushCurrent()
  }

  return params
}

export const normalizeFormulaArgument = rawArgument => {
  const trimmed = String(rawArgument || "").trim()
  const ampersandIndex = getTopLevelAmpersandIndex(trimmed)

  if(ampersandIndex > -1) {
    return `${unwrapQuotedValue(trimmed.slice(0, ampersandIndex))}${trimmed.slice(ampersandIndex + 1).trim()}`
  }

  return unwrapQuotedValue(trimmed)
}

export class Parameters {
  _parameters = {}
  _splittedUserParameters = {}
  _groupedParams = []
  _rawUserParameters = []
  _rawGroupedParams = []

  constructor(builder) {
    this._parameters = builder._parameters
    this._userParameters = builder._userParameters
    this._rawUserParameters = builder._rawUserParameters
    this._rawGroupedParams = builder._rawGroupedParams
    // set up user parameters
    const groupedParamsObject = {}
    const groupedRawParamsObject = {}
    let currentIndex = -1;
    builder._userParameters.forEach((userParameter, index) => {
      const rawUserParameter = String(userParameter)
      const equalIndex = rawUserParameter.indexOf("="),
            key = equalIndex > -1 ? rawUserParameter.slice(0, equalIndex) : rawUserParameter,
            value = equalIndex > -1 ? rawUserParameter.slice(equalIndex + 1) : ""
      if(!key) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid syntax of optional parameter. Syntax: paramName=paramValue`)
      }
      let resolvedValue = this._parameters[key]?.defaultValue
      const rawValue = this._rawUserParameters[index] || String(userParameter)
      if(value !== null && String(value).trim() !== "") {
        resolvedValue = value
      }
      if(CommonService.isJson(value.slice(1, -1))) {
        resolvedValue = CommonService.parseJson(value.slice(1, -1), {});
      }
      if(builder._baseIndexOn !== undefined && builder._baseIndexOn === key) {
        currentIndex++;
      }
      if(builder._groupingParams?.length > 0 && builder._groupingParams.includes(key)) {
        if(!groupedParamsObject[key]) {
          groupedParamsObject[key] = [];
        }
        groupedParamsObject[key].push(resolvedValue);
        if(!groupedRawParamsObject[key]) {
          groupedRawParamsObject[key] = [];
        }
        groupedRawParamsObject[key].push(rawValue);
      } else if(builder._groupingWithIndex?.length > 0 && builder._groupingWithIndex.includes(key)) {
        if(!groupedParamsObject[key]) {
          groupedParamsObject[key] = [];  
        }
        groupedParamsObject[key][currentIndex] = resolvedValue;
        if(!groupedRawParamsObject[key]) {
          groupedRawParamsObject[key] = []
        }
        groupedRawParamsObject[key][currentIndex] = rawValue;
      } else {
        this._parameters[key] = {
          value: resolvedValue,
          optional: true
        }
      }
    })

    Object.entries(groupedParamsObject).forEach(([k, v]) => {
      v.forEach((value, index) => {
        if(!this._groupedParams[index]) {
          this._groupedParams[index] = {}
        }
        this._groupedParams[index][k] = value;
        if(!this._rawGroupedParams[index]) {
          this._rawGroupedParams[index] = {}
        }
        this._rawGroupedParams[index][k] = groupedRawParamsObject[k]?.[index]
      })
    })

    // set up getters to all parameters - seperate loop as we will do here more stuff
    Object.entries(builder._parameters).forEach(([name, obj]) => {
      this[name] = obj.value;
    })
  }

  getSplittedParameters() {
    return this._splittedUserParameters;
  }

  getGroupedParams() {
    return this._groupedParams;
  }

  getParameters() {
    return this._parameters;
  }

  getRawParameters() {
    return this._rawUserParameters.reduce((acc, rawValue, index) => {
      const userParameter = this._userParameters?.[index]
      const equalIndex = String(userParameter).indexOf("="),
            key = equalIndex > -1 ? String(userParameter).slice(0, equalIndex) : String(userParameter)
      if(key) {
        acc[key] = rawValue
      }
      return acc
    }, {})
  }

  getGroupedRawParams() {
    return this._rawGroupedParams
  }

  static get Builder() {
    class Builder {
      _parameters = {}
      _userParameters = []
      _rawUserParameters = []
      _rawGroupedParams = []
      _groupingParams = []
      _baseIndexOn = undefined;
      _groupingWithIndex = [];

      withRequiredParameter = (parameterName, parameterValue) => {
        if(!parameterValue || String(parameterValue).trim() === "") {
          throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} can not be blank!`)
        }
        this._parameters[parameterName] = {
          value: parameterValue,
          optional: false
        };
        return this;
      }

      withOptionalParameter = (parameterName, defaultValue) => {
        this._parameters[parameterName] = {
          defaultValue,
          optional: true
        }
        return this;
      }

      withUserParameters = (userParameters = []) => {
        this._userParameters = userParameters;
        this._rawUserParameters = Array.isArray(userParameters?.rawParams) ? userParameters.rawParams : []
        return this;
      }

      withGrouping = groupingParams => {
        this._groupingParams = groupingParams;
        return this;
      }

      withRequiredArrayParameter = (parameterName, parameterValue) => {
        if(!parameterValue || String(parameterValue).trim() === "") {
          throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} can not be blank!`)
        }
        this._parameters[parameterName] = {
          value: String(parameterValue).split(","),
          optional: false
        }
        return this;
      }

      withRequiredJsonArrayParameter = (parameterName, parameterValue) => {
        if(!parameterValue || String(parameterValue).trim() === "") {
          throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} can not be blank!`)
        }
        if(!CommonService.isJson(parameterValue)) {
          throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} has to be a proper json array!`)
        }
        this._parameters[parameterName] = {
          value: CommonService.parseJson(parameterValue, []),
          optional: false
        }
        return this;
      }

      withRequiredJsonParameter = (parameterName, parameterValue) => {
        if(!parameterValue || String(parameterValue).trim() === "") {
          throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} can not be blank.`)
        }
        let resolvedValue = parameterValue
        if(typeof parameterValue === "string") {
          let normalized = parameterValue.trim()
          if(normalized.startsWith('"') && normalized.endsWith('"')) {
            normalized = normalized.slice(1, -1)
          }
          normalized = normalized.replace(/""/g, '"')
          if(!CommonService.isJson(normalized)) {
            throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Parameter ${parameterName} has to be a proper json object.`)
          }
          resolvedValue = CommonService.parseJson(normalized, {})
        }

        this._parameters[parameterName] = {
          value: resolvedValue,
          optional: false
        }
        return this
      }

      withBaseIndexOn = index => {
        this._baseIndexOn = index;
        return this;
      }

      withGroupingWithIndex = groupingWithIndex => {
        this._groupingWithIndex = groupingWithIndex;
        return this;
      }

      build = () => new Parameters(this)
    }
    return Builder;
  }
}
